import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, ShieldCheck, KeyRound } from 'lucide-react';
import { useAuth } from '../state/AuthContext';
import { LogoWordmark } from '../components/Logo';
import { ApiError, apiGet, apiPost } from '../lib/api';
import { formatDate } from '../lib/format';
import { passkeysSupported, registerPasskey } from '../lib/webauthn';
import { ENTRY_TIER, type Cadence, type PlanSelectionId } from '../lib/plans';
import { useT } from '../i18n';
import { SIGNUP_NOTICE_ITEMS, SIGNUP_NOTICE_TITLE } from '../lib/signupNotice';

// Code-split: the plan catalogue and picker only load when a new user reaches
// the plan-selection step, keeping the initial auth bundle lean.
const PlanPicker = lazy(() => import('./auth/PlanPicker'));

type Mode = 'signin' | 'signup';
type SignupStep = 'account' | 'plan';

const HONORIFICS = ['Mr.', 'Mrs.', 'Ms.'];

// Password policy mirrors the server (assertPasswordStrength): all five rules
// must pass — min 12 chars and at least one upper, lower, digit and special.
const PASSWORD_RULES: { key: string; label: string; test: (pw: string) => boolean }[] = [
  { key: 'length', label: 'At least 12 characters', test: (pw) => pw.length >= 12 },
  { key: 'upper', label: 'One uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
  { key: 'lower', label: 'One lowercase letter', test: (pw) => /[a-z]/.test(pw) },
  { key: 'digit', label: 'One number', test: (pw) => /\d/.test(pw) },
  { key: 'special', label: 'One special character', test: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

function passwordChecks(pw: string) {
  return PASSWORD_RULES.map((rule) => ({ ...rule, ok: rule.test(pw) }));
}

export default function AuthPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { session, login, verifyTwoFactor, loginWithPasskey, signup, requestMinorSignup } = useAuth();
  const t = useT();

  const mode: Mode = params.get('mode') === 'signup' ? 'signup' : 'signin';
  const from = (location.state as { from?: string } | null)?.from ?? '/app';

  const [honorific, setHonorific] = useState('Mr.');
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  // DPDP G12: an explicit, unticked-by-default confirmation. The server refuses
  // a signup without `isAdult: true`; this box is how the user says it.
  const [isAdult, setIsAdult] = useState(false);
  // UNDER 18 (server/minor-accounts.mjs): the only way a minor gets an account —
  // by naming a parent or guardian who already has one, by full name AND their
  // 12-digit Sansara ID. Offered only when the server says the path is switched
  // on (GET /api/auth/signup/options; dark in production until counsel).
  const [minorAvailable, setMinorAvailable] = useState(false);
  const [under18, setUnder18] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [guardianName, setGuardianName] = useState('');
  const [guardianHandle, setGuardianHandle] = useState('');
  // Set once a request is sent, or when sign-in says the guardian has not approved yet.
  const [waitingForGuardian, setWaitingForGuardian] = useState<{ expiresAt: string | null } | null>(null);
  useEffect(() => {
    if (mode !== 'signup') return;
    apiGet<{ minorAccounts: boolean }>('/api/auth/signup/options')
      .then((o) => setMinorAvailable(Boolean(o.minorAccounts)))
      .catch(() => setMinorAvailable(false));
  }, [mode]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<SignupStep>('account');
  // One-shot "why am I here" notice after an idle/server-side sign-out.
  const [sessionLostNotice] = useState<boolean>(() => {
    try {
      const v = sessionStorage.getItem('sansara.sessionLost') === '1';
      sessionStorage.removeItem('sansara.sessionLost');
      return v;
    } catch { return false; }
  });
  const [cadence, setCadence] = useState<Cadence>('monthly');
  // Conv-37: every account starts on the one-month Silver trial, so this is not
  // the tier the account STARTS on — it is what the user says they want after it,
  // recorded as a planSelection. Seeded to the trial's own tier so a signup that
  // never touches the picker records the plan the trial actually continues into.
  const [selectedPlan, setSelectedPlan] = useState<PlanSelectionId>(ENTRY_TIER);
  const [twoFactorChallenge, setTwoFactorChallenge] = useState<string | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  // 2026-10-19: the code is asked for on the first sign-in and every 7 days;
  // between, a passkey is enough. `reverify` = a passkey was accepted but the
  // weekly check is due, so the code step explains why it is back.
  const [reverify, setReverify] = useState(false);
  // After a sign-in WITH a code on a browser that can hold a passkey and an
  // account that has none, offer one before entering the app — that is what
  // makes the next sign-in code-free. `holdNav` stops the session effect from
  // navigating past the offer; it is set BEFORE the session lands.
  const holdNav = useRef(false);
  const [offerPasskey, setOfferPasskey] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);

  useEffect(() => {
    if (session && !holdNav.current) navigate(from, { replace: true });
  }, [session, navigate, from]);

  // Autofill → sign in (founder 2026-10-19: "if it is autofilled then it should
  // automatically try to log in"). A browser autofill fires an input event that
  // is NOT typing — Chrome sends a plain Event, Safari an InputEvent with
  // inputType 'insertReplacementText'. It submits only when the PASSWORD was
  // autofilled and the email was autofilled too or already there at that
  // moment — so an autofilled email followed by a TYPED password never submits
  // half a password. At most once per visit, so a wrong saved password cannot loop.
  const formRef = useRef<HTMLFormElement | null>(null);
  const autoSubmitted = useRef(false);
  const emailAutofilled = useRef(false);
  const passwordAutofilled = useRef(false);
  const emailPresentAtPasswordFill = useRef(false);
  const isAutofill = (ev: unknown) => {
    const native = (ev as { nativeEvent?: Event }).nativeEvent;
    if (!native) return false;
    if (typeof InputEvent !== 'undefined' && native instanceof InputEvent) return native.inputType === 'insertReplacementText' || native.inputType === '';
    return true; // a non-InputEvent `input` is how Chrome reports autofill
  };
  useEffect(() => {
    if (mode !== 'signin' || twoFactorChallenge || autoSubmitted.current || !passwordAutofilled.current) return;
    if (!(emailAutofilled.current || emailPresentAtPasswordFill.current)) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) || !password) return;
    // Flag set when the submit FIRES — set before the timer, any change inside
    // the window cancelled it for good (2026-10-19 review).
    const id = window.setTimeout(() => { autoSubmitted.current = true; formRef.current?.requestSubmit(); }, 150);
    return () => window.clearTimeout(id);
  }, [email, password, mode, twoFactorChallenge]);

  const checks = useMemo(() => passwordChecks(password), [password]);
  const allRulesPass = checks.every((c) => c.ok);
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  // Client-side only — a convenience so "Continue" is not a guaranteed-doomed
  // round trip. The SERVER is the gate (assertSignupInvite); this must never be
  // mistaken for the enforcement boundary.
  const guardianHandleDigits = guardianHandle.replace(/[\s-]/g, '');
  const canSubmitSignup = allRulesPass && passwordsMatch && inviteCode.trim().length > 0 && (under18
    ? Boolean(dateOfBirth) && guardianName.trim().length > 0 && /^\d{12}$/.test(guardianHandleDigits)
    : isAdult);

  const onPasskey = async () => {
    if (!email.trim()) {
      setError(t('Enter your email, then sign in with your passkey.'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await loginWithPasskey(email.trim());
      if (!result.ok && 'challenge' in result) {
        setReverify(true);
        setTwoFactorChallenge(result.challenge);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('Could not sign in with a passkey.'));
    } finally {
      setBusy(false);
    }
  };

  const setMode = (next: Mode) => {
    setError(null);
    setStep('account');
    setParams(next === 'signup' ? { mode: 'signup' } : {}, { replace: true });
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (mode === 'signup' && !canSubmitSignup) {
      setError(t(passwordsMatch ? 'Please satisfy every password rule.' : 'Passwords do not match.'));
      return;
    }
    // First signup screen gathers account basics, then advances to plan choice.
    //
    // Conv-40, founder: it used to advance unconditionally, so "an account already
    // exists for this email" — the one refusal that invalidates everything typed on
    // this screen — only surfaced on the NEXT screen, after the user had also picked
    // a plan. Ask now, while they are still looking at the field they need to change.
    //
    // This is a COURTESY, not a gate. `POST /api/auth/signup` re-checks and is the
    // real boundary, so a check that fails or cannot be reached must never strand a
    // legitimate signup on screen one: any error advances, exactly as before.
    // Under 18: one screen, no plan step (a minor account is always on the free
    // plan), and no session comes back — the request waits for the guardian.
    if (mode === 'signup' && under18) {
      setError(null);
      setBusy(true);
      try {
        const sent = await requestMinorSignup({
          profile: { honorific, firstName: firstName.trim(), surname: surname.trim(), email: email.trim() },
          password,
          inviteCode: inviteCode.trim(),
          minor: { dateOfBirth, guardianName: guardianName.trim(), guardianHandle: guardianHandleDigits },
        });
        setPassword('');
        setConfirmPassword('');
        setWaitingForGuardian({ expiresAt: sent.expiresAt });
      } catch (err) {
        setError(err instanceof ApiError || err instanceof Error ? err.message : t('Something went wrong. Please try again.'));
      } finally {
        setBusy(false);
      }
      return;
    }
    if (mode === 'signup' && step === 'account') {
      setError(null);
      setBusy(true);
      try {
        const check = await apiPost<{ available: boolean }>('/api/auth/signup/check-email', {
          email: email.trim(),
          inviteCode: inviteCode.trim(),
        });
        if (!check.available) {
          setError(t('A Sansara account already exists for this email.'));
          return;
        }
      } catch {
        /* unreachable / rate-limited / bad invite — let signup be the authority */
      } finally {
        setBusy(false);
      }
      setStep('plan');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      if (mode === 'signup') {
        if (!isAdult) throw new Error(t('Confirm that you are 18 or older to continue.'));
        await signup({
          profile: { honorific, firstName: firstName.trim(), surname: surname.trim(), email: email.trim() },
          password,
          inviteCode: inviteCode.trim(),
          isAdult: true,
          plan: { plan: selectedPlan, cadence },
        });
      } else {
        const result = await login(email.trim(), password);
        if (!result.ok && 'guardianPending' in result) {
          setPassword('');
          setWaitingForGuardian({ expiresAt: result.requestExpiresAt });
          setBusy(false);
          return;
        }
        if (!result.ok) {
          setTwoFactorChallenge(result.challenge);
          setBusy(false);
          return;
        }
      }
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : t('Something went wrong. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const onVerifyTwoFactor = async (e: FormEvent) => {
    e.preventDefault();
    if (!twoFactorChallenge) return;
    setError(null);
    setBusy(true);
    try {
      // Hold navigation before the session lands, then decide: offer a passkey
      // only when this browser can hold one and the account has none.
      holdNav.current = passkeysSupported();
      const next = await verifyTwoFactor(twoFactorChallenge, twoFactorCode.trim());
      if (holdNav.current && (next.passkeyCount ?? 0) === 0) {
        setOfferPasskey(true);
        return;
      }
      holdNav.current = false;
      navigate(from, { replace: true });
    } catch (err) {
      // A wrong code must not leave navigation held: a later passkey sign-in on
      // this page would otherwise land a session and stay on the form.
      holdNav.current = false;
      setError(err instanceof ApiError ? err.message : t('Could not verify the code. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const finishSignIn = () => {
    holdNav.current = false;
    navigate(from, { replace: true });
  };
  const addPasskeyAndContinue = async () => {
    setPasskeyBusy(true);
    try {
      await registerPasskey(session?.csrfToken ?? '', 'Passkey');
    } catch {
      /* cancelled or unsupported — the code path still works; carry on */
    } finally {
      setPasskeyBusy(false);
      finishSignIn();
    }
  };

  const cancelTwoFactor = () => {
    holdNav.current = false;
    setReverify(false);
    setTwoFactorChallenge(null);
    setTwoFactorCode('');
    setError(null);
    setPassword('');
  };

  return (
    <div className="flex min-h-dvh flex-col bg-surface-sunken dark:bg-surface-dark">
      <header className="px-6 py-5">
        <Link to="/" aria-label={t('Sansara home')}>
          <LogoWordmark />
        </Link>
      </header>

      <main className="flex flex-1 items-start justify-center px-4 pb-16 pt-4 sm:items-center sm:pt-0">
        <div className={`w-full ${mode === 'signup' && step === 'plan' ? 'max-w-3xl' : 'max-w-md'}`}>
          <div className="card p-7 sm:p-8">
            <h1 className="font-display text-2xl font-bold tracking-tight">
              {waitingForGuardian
                ? t('Waiting for your parent or guardian')
                : offerPasskey
                ? t('Next time, skip the code')
                : twoFactorChallenge
                ? t('Two-factor authentication')
                : mode === 'signup'
                  ? step === 'plan'
                    ? t('Choose your plan')
                    : t('Create your account')
                  : t('Welcome back')}
            </h1>
            <p className="mt-1.5 text-sm text-ink-soft dark:text-slate-400">
              {waitingForGuardian
                ? t('Your account is not open yet.')
                : offerPasskey
                ? t('You are signed in.')
                : twoFactorChallenge
                ? t('Enter the 6-digit code from your authenticator app, or use a backup code.')
                : mode === 'signup'
                  ? step === 'plan'
                    ? t('Billing activates only once payments are switched on — your pick is saved as a pending selection.')
                    : t('Track every asset you own, in one calm place.')
                  : t('Sign in to your Sansara account.')}
            </p>

            {/* Conv-26: honest notice when the previous session ended without
                an explicit sign-out (idle timeout / server-side revoke). The
                flag is stamped by api.ts on a data-route 401 or by the client
                idle watcher; consumed once, shown only on the sign-in form. */}
            {sessionLostNotice && mode === 'signin' && !twoFactorChallenge ? (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300" role="status">
                {t('You were signed out — sessions end after a period of inactivity to protect your data. Sign in to continue.')}
              </div>
            ) : null}

            {waitingForGuardian ? (
              // The ONLY thing a pending under-18 account sees: no data exists yet.
              <div className="mt-6 space-y-4" role="status">
                <p className="text-sm leading-relaxed text-ink dark:text-slate-200">
                  {t('Your parent or guardian needs to approve your account in their own Sansara account, under Household. Until they do, you cannot sign in.')}
                </p>
                {waitingForGuardian.expiresAt ? (
                  <p className="text-sm leading-relaxed text-ink-soft dark:text-slate-300">
                    {t('If they do not approve it by {date}, the request and your details are deleted.', { date: formatDate(waitingForGuardian.expiresAt) })}
                  </p>
                ) : null}
                <p className="text-xs text-ink-faint dark:text-slate-400">{t('Sansara does not send email, so let them know you have asked.')}</p>
                <button type="button" className="btn-secondary w-full" onClick={() => { setWaitingForGuardian(null); setMode('signin'); }}>
                  {t('Back to sign in')}
                </button>
              </div>
            ) : offerPasskey ? (
              <div className="mt-6 space-y-4">
                <p className="text-sm text-ink-soft dark:text-slate-400">
                  {t('Add a passkey — sign in with Face ID, Touch ID or your device unlock instead of your password and authenticator code.')}
                </p>
                <p className="text-xs text-ink-faint dark:text-slate-400">
                  {t('Your authenticator code is still asked for once every 7 days.')}
                </p>
                <button type="button" className="btn-primary w-full" onClick={addPasskeyAndContinue} disabled={passkeyBusy}>
                  <span className="inline-flex items-center gap-2"><KeyRound size={16} /> {passkeyBusy ? t('Please wait…') : t('Add a passkey')}</span>
                </button>
                <button type="button" className="btn-secondary w-full" onClick={finishSignIn} disabled={passkeyBusy}>
                  {t('Not now')}
                </button>
              </div>
            ) : twoFactorChallenge ? (
              <form className="mt-6 space-y-4" onSubmit={onVerifyTwoFactor} noValidate>
                {reverify ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300" role="status">
                    {t('It has been 7 days since you last entered an authenticator code. Enter one now — after that, your passkey alone signs you in for another 7 days.')}
                  </div>
                ) : null}
                <div>
                  <label className="label" htmlFor="twoFactorCode">
                    {t('Authentication code')}
                  </label>
                  <input
                    id="twoFactorCode"
                    className="input mt-1.5 tracking-widest"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value)}
                    inputMode="text"
                    autoComplete="one-time-code"
                    autoFocus
                    placeholder="123456"
                    required
                  />
                </div>
                {error ? (
                  <p className="rounded-xl border border-loss/20 bg-loss/5 px-3.5 py-2.5 text-sm font-medium text-loss dark:text-loss-dark" role="alert">
                    {t(error)}
                  </p>
                ) : null}
                <button type="submit" className="btn-primary w-full" disabled={busy || twoFactorCode.trim().length === 0}>
                  {busy ? t('Verifying…') : t('Verify and sign in')}
                </button>
                <button type="button" className="btn-secondary w-full" onClick={cancelTwoFactor} disabled={busy}>
                  {t('Back to sign in')}
                </button>
                <p className="text-xs text-ink-faint dark:text-slate-400">
                  {t('Lost your authenticator app and your backup codes? Write to support@sansarafin.com from your account email. Once we have checked it is you, we switch two-factor off so you can set it up again.')}
                </p>
              </form>
            ) : (
            <form ref={formRef} className="mt-6 space-y-4" onSubmit={onSubmit} noValidate>
              {mode === 'signup' && step === 'plan' ? (
                <>
                  <Suspense
                    fallback={<div className="py-12 text-center text-sm text-ink-soft dark:text-slate-400">{t('Loading plans…')}</div>}
                  >
                    <PlanPicker
                      cadence={cadence}
                      onCadenceChange={setCadence}
                      selected={selectedPlan}
                      onSelect={setSelectedPlan}
                    />
                  </Suspense>

                  {error ? (
                    <p className="rounded-xl border border-loss/20 bg-loss/5 px-3.5 py-2.5 text-sm font-medium text-loss dark:text-loss-dark" role="alert">
                      {t(error)}
                    </p>
                  ) : null}

                  <div className="flex gap-3">
                    <button type="button" className="btn-secondary" onClick={() => setStep('account')} disabled={busy}>
                      {t('Back')}
                    </button>
                    <button type="submit" className="btn-primary flex-1" disabled={busy}>
                      {busy ? t('Creating account…') : t('Create account')}
                    </button>
                  </div>
                </>
              ) : (
                <>
              {mode === 'signup' ? (
                <>
                  <div>
                    <span className="label">{t('Honorific')}</span>
                    <div className="mt-1.5 flex gap-2">
                      {HONORIFICS.map((h) => (
                        <button
                          key={h}
                          type="button"
                          onClick={() => setHonorific(h)}
                          className={`flex-1 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${
                            honorific === h
                              ? 'border-brand-600 bg-brand-50 text-brand-700 dark:border-brand-400 dark:bg-brand-500/10 dark:text-brand-300'
                              : 'border-slate-200 text-ink-soft hover:border-slate-300 dark:border-slate-700 dark:text-slate-400'
                          }`}
                        >
                          {/* The STORED value stays the English honorific (setHonorific(h)
                              above sends `h` verbatim) — only the label is translated. */}
                          {t(h)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="label" htmlFor="firstName">
                        {t('First name')}
                      </label>
                      <input
                        id="firstName"
                        className="input mt-1.5"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        autoComplete="given-name"
                        required
                      />
                    </div>
                    <div>
                      <label className="label" htmlFor="surname">
                        {t('Surname')}
                      </label>
                      <input
                        id="surname"
                        className="input mt-1.5"
                        value={surname}
                        onChange={(e) => setSurname(e.target.value)}
                        autoComplete="family-name"
                        required
                      />
                    </div>
                  </div>
                </>
              ) : null}

              <div>
                <label className="label" htmlFor="email">
                  {t('Email')}
                </label>
                <input
                  id="email"
                  type="email"
                  className="input mt-1.5"
                  value={email}
                  onChange={(e) => { if (isAutofill(e)) emailAutofilled.current = true; setEmail(e.target.value); }}
                  autoComplete="email"
                  required
                />
              </div>

              <div>
                <label className="label" htmlFor="password">
                  {t('Password')}
                </label>
                <input
                  id="password"
                  type="password"
                  className="input mt-1.5"
                  value={password}
                  onChange={(e) => { if (isAutofill(e)) { passwordAutofilled.current = true; emailPresentAtPasswordFill.current = email.trim().length > 0; } setPassword(e.target.value); }}
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  required
                />
                {mode === 'signup' ? (
                  <ul className="mt-2 space-y-1 text-xs text-ink-faint dark:text-slate-400" aria-live="polite">
                    {checks.map((c) => (
                      <li
                        key={c.key}
                        className={`flex items-center gap-1.5 transition-colors ${c.ok ? 'text-gain dark:text-gain-dark' : ''}`}
                      >
                        <Check size={13} className={c.ok ? '' : 'opacity-30'} aria-hidden="true" />
                        <span>{t(c.label)}</span>
                        <span className="sr-only">{t(c.ok ? '— met' : '— not met')}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>

              {mode === 'signup' ? (
                <div>
                  <label className="label" htmlFor="confirmPassword">
                    {t('Confirm password')}
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    className="input mt-1.5"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                  <p
                    className={`mt-2 flex items-center gap-1.5 text-xs text-ink-faint dark:text-slate-400 ${passwordsMatch ? 'text-gain dark:text-gain-dark' : ''}`}
                    aria-live="polite"
                  >
                    <Check size={13} className={passwordsMatch ? '' : 'opacity-30'} aria-hidden="true" />
                    <span>{t('Passwords match')}</span>
                    {confirmPassword.length > 0 && !passwordsMatch ? <span className="sr-only">{t('— not yet')}</span> : null}
                  </p>
                </div>
              ) : null}

              {/* On the ACCOUNT step, not the plan step: the server rejects a bad
                  code, and making someone fill in their name, email, password AND
                  pick a plan before telling them is a bad trade. Factual copy —
                  no "limited spots" / "exclusive access" scarcity framing. */}
              {mode === 'signup' && step === 'account' ? (
                <div>
                  <label className="label" htmlFor="inviteCode">
                    {t('Invite code')}
                  </label>
                  <input
                    id="inviteCode"
                    type="text"
                    className="input mt-1.5"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    autoComplete="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    required
                  />
                  <p className="mt-2 text-xs text-ink-faint dark:text-slate-400">
                    {t('Sansara is invite-only while we are in early access.')}
                  </p>
                </div>
              ) : null}

              {/* DPDP G1 — the short itemised notice, BEFORE the user agrees to
                  anything (Rule 3). A summary of the Privacy Policy, linked in
                  full. Collapsed so the form stays one screen; the summary line
                  says what is inside. */}
              {mode === 'signup' && step === 'account' ? (
                <details className="group rounded-xl border border-slate-200 bg-surface-sunken/60 px-3.5 py-2.5 text-sm dark:border-slate-700 dark:bg-slate-800/40">
                  <summary className="cursor-pointer select-none font-semibold text-ink dark:text-slate-200">
                    {t(SIGNUP_NOTICE_TITLE)}
                  </summary>
                  <dl className="mt-2 space-y-2 text-xs leading-relaxed text-ink-soft dark:text-slate-300">
                    {SIGNUP_NOTICE_ITEMS.map((item) => (
                      <div key={item.heading}>
                        <dt className="font-semibold text-ink dark:text-slate-200">{t(item.heading)}</dt>
                        <dd>{t(item.text)}</dd>
                      </div>
                    ))}
                  </dl>
                  <p className="mt-2 text-xs">
                    <Link className="font-semibold text-brand-700 underline dark:text-brand-400" to="/privacy">{t('Read the full Privacy Policy')}</Link>
                  </p>
                </details>
              ) : null}

              {/* Under 18 — the guardian-linked path (minor-accounts.mjs). The
                  date of birth is asked ONLY here: an adult account never stores one. */}
              {mode === 'signup' && step === 'account' && under18 ? (
                <div className="space-y-3 rounded-xl border border-slate-200 p-3.5 dark:border-slate-700">
                  <p className="text-sm font-semibold text-ink dark:text-slate-200">{t('Link your parent or guardian')}</p>
                  <p className="text-xs leading-relaxed text-ink-soft dark:text-slate-300">
                    {t('They must already have a Sansara account. Enter their full name and their 12-digit Sansara ID exactly as they appear in their account. They approve your account before you can use it.')}
                  </p>
                  <div>
                    <label className="label" htmlFor="dateOfBirth">{t('Your date of birth')}</label>
                    <input id="dateOfBirth" type="date" className="input mt-1.5" value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} autoComplete="bday" required />
                  </div>
                  <div>
                    <label className="label" htmlFor="guardianName">{t('Parent or guardian’s full name')}</label>
                    <input id="guardianName" className="input mt-1.5" value={guardianName} onChange={(e) => setGuardianName(e.target.value)} autoComplete="off" required />
                  </div>
                  <div>
                    <label className="label" htmlFor="guardianHandle">{t('Their Sansara ID')}</label>
                    <input id="guardianHandle" className="input mt-1.5 font-mono tracking-wider" value={guardianHandle} onChange={(e) => setGuardianHandle(e.target.value)} inputMode="numeric" autoComplete="off" placeholder="1234 5678 9012" required />
                  </div>
                  <p className="text-xs text-ink-faint dark:text-slate-400">
                    {t('An account for someone under 18 is on the free plan. It has no AI features, step rewards, broker linking, sharing, paid plans or advice. Your parent or guardian can see your holdings and account activity until you turn 18.')}
                  </p>
                  <button type="button" className="text-xs font-semibold text-brand-700 underline dark:text-brand-400" onClick={() => { setUnder18(false); setError(null); }}>
                    {t('I am 18 or older')}
                  </button>
                </div>
              ) : null}

              {/* DPDP G12 — 18 or older, said with a clear act. */}
              {mode === 'signup' && step === 'account' && !under18 ? (
                <label className="flex items-start gap-2.5 text-sm text-ink dark:text-slate-200" htmlFor="isAdult">
                  <input
                    id="isAdult"
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 accent-brand-700"
                    checked={isAdult}
                    onChange={(e) => setIsAdult(e.target.checked)}
                  />
                  <span>{t('I am 18 or older')}</span>
                </label>
              ) : null}
              {mode === 'signup' && step === 'account' && !under18 && minorAvailable ? (
                <button type="button" className="text-sm font-semibold text-brand-700 underline dark:text-brand-400" onClick={() => { setUnder18(true); setIsAdult(false); setError(null); }}>
                  {t('I am under 18 — link my parent or guardian')}
                </button>
              ) : null}

              {error ? (
                <p className="rounded-xl border border-loss/20 bg-loss/5 px-3.5 py-2.5 text-sm font-medium text-loss dark:text-loss-dark" role="alert">
                  {t(error)}
                </p>
              ) : null}

              {/* Conv-57 R11-B — PASSKEY-FIRST on sign-in. The panel's onboarding
                  finding: the password wall is the first thing a returning user
                  meets. Returning users who created a passkey get it as the
                  FIRST action, above the password form, with a plain divider —
                  the password path stays one glance away. */}
              {mode === 'signin' && passkeysSupported() ? (
                <>
                  <button
                    type="button"
                    className="btn-primary w-full"
                    onClick={onPasskey}
                    disabled={busy}
                  >
                    <span className="inline-flex items-center gap-2"><KeyRound size={16} /> {t('Sign in with a passkey')}</span>
                  </button>
                  <p className="text-center text-xs text-ink-faint dark:text-slate-400">
                    {t('or sign in with your email and password')}
                  </p>
                </>
              ) : null}

              <button type="submit" className={mode === 'signin' && passkeysSupported() ? 'btn-secondary w-full' : 'btn-primary w-full'} disabled={busy || (mode === 'signup' && !canSubmitSignup)}>
                {busy ? t('Please wait…') : mode === 'signup' ? (under18 ? t('Send the request to my guardian') : t('Continue')) : t('Sign in with email')}
              </button>
                </>
              )}
            </form>
            )}

            {twoFactorChallenge || offerPasskey || waitingForGuardian ? null : (
            <p className="mt-5 text-center text-sm text-ink-soft dark:text-slate-400">
              {mode === 'signup' ? (
                <>
                  {t('Already have an account?')}{' '}
                  <button className="font-semibold text-brand-700 hover:underline dark:text-brand-400" onClick={() => setMode('signin')}>
                    {t('Sign in')}
                  </button>
                </>
              ) : (
                <>
                  {t('New to Sansara?')}{' '}
                  <button className="font-semibold text-brand-700 hover:underline dark:text-brand-400" onClick={() => setMode('signup')}>
                    {t('Create an account')}
                  </button>
                </>
              )}
            </p>
            )}
          </div>

          <p className="mt-5 flex items-start justify-center gap-2 px-4 text-center text-xs leading-relaxed text-ink-faint dark:text-slate-400">
            <ShieldCheck size={14} className="mt-0.5 shrink-0" />
            <span>
              {/* A minor cannot contract: on the under-18 path the guardian accepts
                  the Terms when they approve (Terms → Eligibility). */}
              {under18 ? `${t('Your parent or guardian accepts our Terms for you when they approve your account.')} ${t('Read our')}` : t('By continuing you agree to our')} <Link className="underline" to="/terms">{t('Terms')}</Link>,{' '}
              <Link className="underline" to="/privacy">{t('Privacy Policy')}</Link> {t('and')}{' '}
              <Link className="underline" to="/risk-disclosure">{t('Risk Disclosure')}</Link>. {t('Sansara is a factual market-information and portfolio-tracking platform — we do not provide investment advice.')}
            </span>
          </p>
        </div>
      </main>
    </div>
  );
}
